import React from "react";
import { useCustomerContext } from "contexts/Customer";
import { Button, Grid, MenuItem, Select, TextField } from "@mui/material";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { xorBy, unionBy } from "lodash";

import "./style.scss";

type Props = {
  cancelSearch: () => void;
  confirmFilter: (data) => void;
};

export const AdvanceSearch: React.FC<Props> = ({
  cancelSearch,
  confirmFilter,
}) => {
  const { searchFields, updateSearchFields } = useCustomerContext();
  const availableSearchField = searchFields.filter((f) => f.isAvailable);
  // const { sec_permissions } = useAuthContext();
  const { control, handleSubmit, reset, getValues, setValue, watch } = useForm({
    defaultValues: {
      fields: availableSearchField.filter((f) => f.isSelected),
    },
  });
  const { fields, append, update } = useFieldArray({
    control,
    name: "fields",
  });

  const handleAddMore = () => {
    const currentValue = getValues();
    const freeSearchField = xorBy(currentValue.fields, availableSearchField, "name");

    if (freeSearchField.length !== 0) {
      freeSearchField[0].isSelected = true;
      append(freeSearchField[0]);
    }
  };

  const handleClear = () => {
    const resetedSearchField = availableSearchField.map((field) => {
      if (
        field.name === "City" ||
        field.name === "State" ||
        field.name === "Country" ||
        field.name === "Master Customer Id"
      ) {
        field.isSelected = false;
      } else {
        field.isSelected = true;
      }

      return {
        ...field,
        value: "",
      };
    });

    updateSearchFields(resetedSearchField);
    reset({ fields: resetedSearchField.filter((field) => field.isSelected) });
  };

  const onSubmit = (data) => {
    const mergedData = unionBy(data.fields, availableSearchField, "name");

    updateSearchFields(mergedData);
    confirmFilter(mergedData);
  };

  React.useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      updateSearchFields(
        unionBy(
          value.fields?.map((field: any, index) => {
            if (field?.name === 'Master Customer Id') {
              field.searchType = 'Equal to';

              if (name?.includes('name')) {
                update(index, field);
              }
            }
            
            return field;
          }),
          availableSearchField.map((field) => ({
            ...field,
            isSelected: false,
            value: "",
          })),
          "name"
        )
      );
    });
    
    return () => subscription.unsubscribe();
  }, [watch, availableSearchField, updateSearchFields, update]);

  return (
    <div className="advance-search">
      <h2 className="advance-search-title">Advance Search</h2>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3} mb={3}>
          {fields.map((item, index) => (
            <React.Fragment key={item.id}>
              <Grid item xs={3.5}>
                <Controller
                  name={`fields.${index}.name`}
                  control={control}
                  render={({ field }) => (
                    <Select fullWidth size="small" {...field}>
                      {availableSearchField.map((searchField, i) => (
                        <MenuItem
                          value={searchField.name}
                          key={`${item.id}-${i}`}
                          disabled={searchField.isSelected}
                        >
                          {searchField.name}
                        </MenuItem>
                      ))}
                      ;
                    </Select>
                  )}
                />
              </Grid>
              <Grid item xs={3.5}>
                <Controller
                  name={`fields.${index}.searchType`}
                  control={control}
                  render={({ field }) => (
                    <Select fullWidth size="small" {...field}>
                      <MenuItem value="Equal to">Equal to</MenuItem>
                      {item.name !== 'Master Customer Id' && (
                        <MenuItem value="Start with">Start with</MenuItem>
                      )}
                    </Select>
                  )}
                />
              </Grid>
              <Grid item xs={5}>
                <Controller
                  name={`fields.${index}.value`}
                  control={control}
                  render={({ field }) => (
                    <TextField fullWidth size="small" {...field} />
                  )}
                />
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
        <Grid container spacing={3}>
          <Grid item>
            <Button
              variant="outlined"
              type="button"
              onClick={handleAddMore}
              disabled={
                getValues().fields.length === availableSearchField.length
              }
            >
              Add More
            </Button>
          </Grid>
          <Grid item>
            <Button variant="outlined" type="reset" onClick={handleClear}>
              Clear
            </Button>
          </Grid>
          <Grid item xs />
          <Grid item>
            <Button variant="outlined" type="button" onClick={cancelSearch}>
              Close
            </Button>
          </Grid>
          <Grid item>
            <Button variant="contained" type="submit" disableElevation>
              Search
            </Button>
          </Grid>
        </Grid>
      </form>
    </div>
  );
};
